import { formatMoney } from "@/lib/format";
import type { Campaign } from "@/hooks/useMarketplaceCampaigns";
import type { MyInvitation } from "@/hooks/useCollaborations";

/**
 * One row on the creator's opportunities list, however it arrived.
 *
 * Invitations and open campaigns are two different queries with two different
 * shapes, and the screen treats them as one list because a creator does not
 * care which table a chance came from. Normalising here rather than in the JSX
 * is also what keeps the money label honest — see `payoutLabel`.
 */
export interface Opportunity {
  key: string;
  campaign: Campaign;
  brandName: string;
  brandLogo: string | null;
  /** True when a brand addressed this creator by name. */
  invited: boolean;
  message: string | null;
  /** Already formatted, or null when nobody has named a figure. */
  payout: string | null;
  /** The numeric value behind `payout`, for the running total. Null when unknown. */
  payoutValue: number | null;
  deadline: { label: string; urgent: boolean; at: Date } | null;
}

/**
 * What the creator will actually be paid, said only when it is known.
 *
 * A campaign carries `rateMin`/`rateMax` when the brand set a band, and a
 * `budgetTotal` for the whole campaign — which is NOT one creator's fee and
 * must never be shown as one. Where no per-creator figure exists the row says
 * "vos ponés la tarifa", which is the truth: `acceptInvitation` takes the
 * creator's own `proposedRate` and the offer chain settles it from there.
 *
 * The label is built here, not in the JSX, so `rate === 0` can never render as
 * "USD 0" or "USD Canje".
 */
function payoutLabel(campaign: Campaign): { label: string | null; value: number | null } {
  const { rateMin, rateMax, currency } = campaign;

  if (rateMin && rateMax && rateMax > rateMin) {
    return {
      label: `${formatMoney(rateMin, currency)} – ${formatMoney(rateMax, currency)!.replace(`${currency} `, "")}`,
      // The bottom of the band, so a running total across offers never
      // over-promises.
      value: rateMin,
    };
  }

  const single = rateMin || rateMax;
  if (single && single > 0) return { label: formatMoney(single, currency), value: single };

  return { label: null, value: null };
}

function deadlineFor(
  raw: string | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): Opportunity["deadline"] {
  if (!raw) return null;
  const at = new Date(raw);
  if (Number.isNaN(at.getTime())) return null;

  const days = Math.ceil((at.getTime() - Date.now()) / 86_400_000);

  if (days < 0) return { label: t("marketplace.opportunities.expired"), urgent: true, at };
  if (days === 0) return { label: t("marketplace.opportunities.expiresToday"), urgent: true, at };

  return {
    label: t("marketplace.opportunities.expires", {
      date: at.toLocaleDateString("es", { day: "numeric", month: "short" }),
    }),
    // Under a week is when a creator needs to act, and the row turns amber.
    urgent: days <= 7,
    at,
  };
}

export function fromInvitation(
  invitation: MyInvitation,
  t: (key: string, options?: Record<string, unknown>) => string,
): Opportunity | null {
  const campaign = invitation.campaign;
  if (!campaign) return null;

  const { label, value } = payoutLabel(campaign as Campaign);

  return {
    key: `invitation:${invitation.id}`,
    campaign: campaign as Campaign,
    brandName: campaign.brandProfile?.companyName ?? "—",
    brandLogo: campaign.brandProfile?.logoUrl ?? null,
    invited: true,
    message: invitation.message,
    payout: label,
    payoutValue: value,
    deadline: deadlineFor(invitation.expiresAt, t),
  };
}

export function fromOpenCampaign(
  campaign: Campaign,
  t: (key: string, options?: Record<string, unknown>) => string,
): Opportunity {
  const { label, value } = payoutLabel(campaign);

  return {
    key: `campaign:${campaign.id}`,
    campaign,
    brandName: campaign.brandProfile?.companyName ?? "—",
    brandLogo: campaign.brandProfile?.logoUrl ?? null,
    invited: false,
    message: null,
    payout: label,
    payoutValue: value,
    deadline: deadlineFor(campaign.applicationDeadline, t),
  };
}

/**
 * The two live figures in the list header.
 *
 * Amounts are only summed within a single currency — there is no platform
 * currency, and adding a UYU rate to a USD one produces a number that means
 * nothing. When the visible offers span more than one, the total is withheld
 * rather than guessed.
 */
export function summarise(opportunities: Opportunity[]): {
  total: string | null;
  soonest: Opportunity["deadline"];
} {
  const priced = opportunities.filter((o) => o.payoutValue !== null);
  const currencies = new Set(priced.map((o) => o.campaign.currency));

  const total =
    priced.length > 0 && currencies.size === 1
      ? formatMoney(
          priced.reduce((sum, o) => sum + (o.payoutValue ?? 0), 0),
          [...currencies][0],
        )
      : null;

  const soonest = opportunities
    .map((o) => o.deadline)
    .filter((d): d is NonNullable<Opportunity["deadline"]> => d !== null)
    .sort((a, b) => a.at.getTime() - b.at.getTime())[0];

  return { total, soonest: soonest ?? null };
}
